import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import SvgIcon from '@material-ui/core/SvgIcon';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import Icons from '../../helpers/IconMapping';

import { JobStatus, LabelStatus, LabelStatusToStringMap } from '../../constants';
import { isSupervisor } from '../../helpers/utils';
import { buildFileDownloadUrl } from '../../api';
import { updateLabelDVTStatus } from '../../reducers/labellerSlice';
import { useUser } from '../../contexts/UserContext';
import ActionButton from '../ui/ActionButton';
import useStyles from './styles/AssignmentBoard';
import { Popover } from '@material-ui/core';

function getLabelCheckStatus(item) {
  const checkJobs = item.label_check_jobs;
  if (checkJobs.length == 0) {
    return null;
  }
  return checkJobs[0].status;
}

function getLabelCheckMessage(item) {
  const checkJobs = item.label_check_jobs;
  if (checkJobs.length == 0) {
    return null;
  }
  const job = checkJobs[0];
  return `Job ${job.id} failed with: ${job.status_message}`;
}

function LabelCheckIndicator({ item }) {
  const message = getLabelCheckMessage(item);
  const status = getLabelCheckStatus(item);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    if (message) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  function renderText() {
    if (status == JobStatus.FAILED) {
      return 'Label check failed';
    }
    return '';
  }

  function renderIcon() {
    if (status == JobStatus.FAILED) {
      return <Icons.WarningIcon />;
    }
    return <></>;
  }

  const text = renderText();

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
      {renderIcon()}
      <span onClick={handleClick}>{text}</span>
      <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose}>
        <Typography style={{ padding: '8px' }}>{getLabelCheckMessage(item)}</Typography>
      </Popover>
    </div>
  );
}

function LabelDVTSubmission({ idx, item }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useUser();

  const iconMap = {
    [LabelStatus.REVIEW]: classes.dotIconNeedsReview, // yellowOrange,
    [LabelStatus.ACCEPTED]: classes.dotIconAccepted, // lightGreen,
    [LabelStatus.REJECTED]: classes.dotIconRejected, // red,
  };

  function handleClickApprove() {
    dispatch(updateLabelDVTStatus({ labelDVTId: item.id, status: LabelStatus.ACCEPTED }))
      .then(unwrapResult)
      .catch(() => {
        throw new Error();
      });
  }

  function handleClickRequestChange() {
    dispatch(updateLabelDVTStatus({ labelDVTId: item.id, status: LabelStatus.REJECTED }))
      .then(unwrapResult)
      .catch(() => {
        throw new Error();
      });
  }

  return (
    <Paper className={classes.paperStrip} elevation={4}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <SvgIcon className={iconMap[item.status]} fontSize="large">
            <MoreHorizIcon />
          </SvgIcon>
        </Grid>
        <Grid item className={classes.grow}>
          <Typography component="div">
            <Box fontSize={18}>{`#${idx + 1} - ${LabelStatusToStringMap[LabelStatus.REVIEW]}`}</Box>
          </Typography>
        </Grid>
        <Grid item>
          <LabelCheckIndicator item={item} />
        </Grid>
        {isSupervisor(user.role) && (
          <>
            <Grid item>
              <ActionButton
                label="APPROVE"
                disabled={item.status === LabelStatus.ACCEPTED}
                color="confirm"
                clickHandler={handleClickApprove}
              />
            </Grid>
            <Grid item>
              <ActionButton
                label="REQUEST CHANGE"
                disabled={item.status === LabelStatus.REJECTED}
                color="reject"
                clickHandler={handleClickRequestChange}
              />
            </Grid>
          </>
        )}
        <Grid item>
          <ActionButton
            label="DOWNLOAD"
            color="action"
            icon="GetAppIcon"
            href={buildFileDownloadUrl(item.file.id)}
          />
        </Grid>
        <Grid item>
          <Typography component="div">
            <Box fontSize={10}>{item.created}</Box>
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

LabelDVTSubmission.propTypes = {
  idx: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
};

export default LabelDVTSubmission;
